import axios, {AxiosInstance, AxiosResponse, AxiosError} from 'axios';
import settings from '../settings';

/**
 * Custom API Error class for better error handling
 */
export class CustomApiError extends Error {
    public status?: number;
    public code?: string;
    public data?: any;

    constructor(message: string, status?: number, code?: string, data?: any) {
        super(message);
        this.name = 'CustomApiError';
        this.status = status;
        this.code = code;
        this.data = data;
    }
}

/**
 * Base API client with centralized configuration and error handling
 */
class ApiClient {
    private client: AxiosInstance;
    private token: string | null = null;
    private onTokenUpdated: ((token: string) => void) | null = null;
    private isRefreshing = false;
    private failedQueue: Array<{resolve: (value?: any) => void; reject: (reason?: any) => void}> = [];
    private refreshTimer: number | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: settings.apiURL,
            timeout: 30000, // 30 seconds
            withCredentials: true, // Enable cookies for refresh token
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Initialize token from localStorage if available
        this.initializeToken();
        this.setupInterceptors();
    }

    /**
     * Initialize token from localStorage on startup
     */
    private initializeToken(): void {
        try {
            const storedToken = localStorage.getItem('token');

            if (storedToken) {
                // Try to parse as JSON first, fallback to direct string
                try {
                    this.token = JSON.parse(storedToken);
                } catch {
                    this.token = storedToken;
                }

                // Start proactive refresh timer if we have a token
                this.startRefreshTimer();
            }
        } catch {
            // Silently handle localStorage parse errors
        }
    }

    /**
     * Get the subdomain override for development/testing
     * This is the same logic as useTenant.tsx to ensure consistency
     * NOTE: Only works in non-production environments for security
     */
    private getSubdomainOverride(): string | null {
        // Only allow subdomain override in development/staging environments
        const environment = import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || 'development';
        const isProduction = environment === 'production';

        if (isProduction) {
            // Clear any existing override in production (security measure)
            if (typeof window !== 'undefined') {
                localStorage.removeItem('dev_tenant_subdomain');
            }
            return null;
        }

        // Check URL parameter first (e.g., ?subdomain=testorg)
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const urlSubdomain = urlParams.get('subdomain');
            if (urlSubdomain) {
                return urlSubdomain;
            }
        }

        // Check environment variable (e.g., VITE_TENANT_SUBDOMAIN=testorg)
        const envSubdomain = import.meta.env.VITE_TENANT_SUBDOMAIN;
        if (envSubdomain) {
            return envSubdomain;
        }

        // Check localStorage for persisted subdomain (for convenience)
        if (typeof window !== 'undefined') {
            const storedSubdomain = localStorage.getItem('dev_tenant_subdomain');
            if (storedSubdomain) {
                return storedSubdomain;
            }
        }

        return null;
    }

    /**
     * Set up request and response interceptors
     */
    private setupInterceptors(): void {
        // Request interceptor to add auth token and tenant header
        this.client.interceptors.request.use(
            (config) => {
                // Add authorization token if available
                if (this.token) {
                    config.headers.Authorization = `Bearer ${this.token}`;
                }

                // Add tenant subdomain header for development/testing
                // This is only needed when running locally with subdomain override
                const subdomainOverride = this.getSubdomainOverride();
                if (subdomainOverride) {
                    config.headers['X-Tenant-Subdomain'] = subdomainOverride;
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling and token refresh
        this.client.interceptors.response.use(
            (response: AxiosResponse) => {
                return response;
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as any;

                // Check if error is due to expired JWT token (returns 500 with "jwt expired" message)
                const responseData = error.response?.data as any;
                const errorMessage = responseData?.error || responseData?.message || '';
                const isTokenExpired =
                    typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('jwt expired');

                // Skip token refresh for auth endpoints (login, register, password reset, etc.)
                const authEndpoints = [
                    '/api/login',
                    '/api/register',
                    '/api/auth',
                    '/api/password-reset',
                    '/api/reset-password',
                    '/api/verify',
                    '/api/refresh', // Don't retry refresh endpoint itself
                ];
                const isAuthEndpoint = authEndpoints.some((endpoint) => originalRequest?.url?.includes(endpoint));

                // Handle JWT expiration - try refresh token first (but skip for auth endpoints)
                if (
                    (error.response?.status === 401 || (error.response?.status === 500 && isTokenExpired)) &&
                    !originalRequest._retry &&
                    !isAuthEndpoint
                ) {
                    if (this.isRefreshing) {
                        // If already refreshing, queue this request
                        return new Promise((resolve, reject) => {
                            this.failedQueue.push({resolve, reject});
                        })
                            .then(() => {
                                return this.client.request(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    try {
                        const newToken = await this.refreshAccessToken();
                        this.processFailedQueue(null);

                        // Retry the original request with new token
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return this.client.request(originalRequest);
                    } catch (refreshError) {
                        this.processFailedQueue(refreshError);
                        this.logoutUser();
                        return Promise.reject(this.handleApiError(error));
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(this.handleApiError(error));
            }
        );
    }

    /**
     * Refresh the access token using the httpOnly refresh cookie
     */
    private async refreshAccessToken(): Promise<string> {
        // Prepare headers with subdomain override if present
        const headers: Record<string, string> = {};
        const subdomainOverride = this.getSubdomainOverride();
        if (subdomainOverride) {
            headers['X-Tenant-Subdomain'] = subdomainOverride;
        }

        // Use raw axios here to avoid circular dependency (this is called from interceptor)
        const response = await axios.post(
            `${settings.apiURL}/api/refresh`,
            {},
            {
                withCredentials: true, // Send httpOnly refresh cookie
                headers,
            }
        );

        const result = response.data.result || response.data;
        const newToken = result.token;

        if (!newToken) {
            throw new Error('No token returned from refresh endpoint');
        }

        // Update access token
        this.setToken(newToken);

        return newToken;
    }

    /**
     * Process the queue of failed requests after token refresh
     */
    private processFailedQueue(error: any): void {
        this.failedQueue.forEach(({resolve, reject}) => {
            if (error) {
                reject(error);
            } else {
                resolve(null);
            }
        });

        this.failedQueue = [];
    }

    /**
     * Logout user and clear all data
     */
    private logoutUser(): void {
        // Clear tokens and all client-side data
        this.clearTokens();

        try {
            // Clear all client-side data (security measure)
            localStorage.clear();
            sessionStorage.clear();

            // Clear all cookies
            document.cookie.split(';').forEach(function (c) {
                document.cookie = c
                    .replace(/^ +/, '')
                    .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
            });
        } catch {
            // Silently handle data clearing errors
        }

        // Redirect to login
        window.location.href = '/login';
    }

    /**
     * Handle API errors and convert them to CustomApiError
     */
    private handleApiError(error: AxiosError): CustomApiError {
        if (error.response) {
            // Server responded with error status
            const {status, data} = error.response;
            const message = (data as any)?.error || error.message || 'An error occurred';

            // Handle specific error cases
            switch (status) {
                case 401: {
                    // Only clear token if this is NOT an auth endpoint (login, register, etc.)
                    // For auth endpoints, 401 is expected for invalid credentials
                    const authEndpoints = [
                        '/api/login',
                        '/api/register',
                        '/api/auth',
                        '/api/password-reset',
                        '/api/reset-password',
                        '/api/verify',
                    ];
                    const isAuthEndpoint = authEndpoints.some((endpoint) => error.config?.url?.includes(endpoint));

                    if (!isAuthEndpoint) {
                        this.clearToken(); // Clear invalid token
                    }

                    return new CustomApiError('Authentication required', status, 'UNAUTHORIZED', data);
                }
                case 403:
                    return new CustomApiError('Access forbidden', status, 'FORBIDDEN', data);
                case 404:
                    return new CustomApiError('Resource not found', status, 'NOT_FOUND', data);
                case 422:
                    return new CustomApiError('Validation failed', status, 'VALIDATION_ERROR', data);
                case 429:
                    return new CustomApiError('Too many requests', status, 'RATE_LIMITED', data);
                case 500:
                    return new CustomApiError('Server error', status, 'SERVER_ERROR', data);
                default:
                    return new CustomApiError(message, status, 'API_ERROR', data);
            }
        } else if (error.request) {
            // Network error
            return new CustomApiError('Network error - please check your connection', undefined, 'NETWORK_ERROR');
        } else {
            // Something else happened
            return new CustomApiError(error.message || 'An unexpected error occurred', undefined, 'UNKNOWN_ERROR');
        }
    }

    /**
     * Set authentication token (backward compatibility)
     */
    setToken(token: string): void {
        this.token = token;
        // Persist to localStorage
        try {
            localStorage.setItem('token', token);
        } catch {
            // Silently handle localStorage save errors
        }

        // Start proactive refresh timer
        this.startRefreshTimer();

        // Notify auth context of token update
        if (this.onTokenUpdated) {
            this.onTokenUpdated(token);
        }
    }

    /**
     * Start proactive token refresh timer
     * Refreshes token 2 minutes before expiration (13 minutes for 15-minute tokens)
     */
    private startRefreshTimer(): void {
        // Clear existing timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }

        // Set timer for 13 minutes (refresh 2 minutes before 15-minute token expires)
        this.refreshTimer = setTimeout(
            async () => {
                try {
                    await this.refreshAccessToken();
                    // Restart timer for the new token
                    this.startRefreshTimer();
                } catch (error) {
                    console.warn('Proactive token refresh failed:', error);
                    // Don't logout here - let the user continue until they make a request that fails
                    // The interceptor will handle the refresh at that point
                }
            },
            13 * 60 * 1000
        ); // 13 minutes in milliseconds
    }

    /**
     * Set callback for token updates (used by auth context)
     */
    setTokenUpdatedCallback(callback: (token: string) => void): void {
        this.onTokenUpdated = callback;
    }

    /**
     * Clear authentication token (backward compatibility)
     */
    clearToken(): void {
        this.token = null;

        // Clear refresh timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        // Remove from localStorage
        try {
            localStorage.removeItem('token');
        } catch {
            // Silently handle localStorage removal errors
        }

        // Notify auth context of token removal
        if (this.onTokenUpdated) {
            this.onTokenUpdated('');
        }
    }

    /**
     * Clear tokens and cleanup
     */
    clearTokens(): void {
        this.token = null;

        // Clear refresh timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        // Remove from localStorage
        try {
            localStorage.removeItem('token');
        } catch {
            // Silently handle localStorage removal errors
        }

        // Notify auth context of token removal
        if (this.onTokenUpdated) {
            this.onTokenUpdated('');
        }
    }

    /**
     * Refresh token from localStorage (useful when auth state changes)
     */
    refreshTokenFromStorage(): void {
        this.initializeToken();
    }

    /**
     * Get current token
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Manually trigger token refresh (useful for testing or manual refresh)
     */
    async manualRefresh(): Promise<string | null> {
        try {
            return await this.refreshAccessToken();
        } catch (error) {
            console.error('Manual token refresh failed:', error);
            return null;
        }
    }

    /**
     * Generic GET request
     */
    async get<T>(url: string, config?: any): Promise<T> {
        const response = await this.client.get<T>(url, config);
        return response.data;
    }

    /**
     * Generic POST request
     */
    async post<T>(url: string, data?: any, config?: any): Promise<T> {
        const response = await this.client.post<T>(url, data, config);
        return response.data;
    }

    /**
     * Generic PUT request
     */
    async put<T>(url: string, data?: any, config?: any): Promise<T> {
        const response = await this.client.put<T>(url, data, config);
        return response.data;
    }

    /**
     * Generic PATCH request
     */
    async patch<T>(url: string, data?: any, config?: any): Promise<T> {
        const response = await this.client.patch<T>(url, data, config);
        return response.data;
    }

    /**
     * Generic DELETE request
     */
    async delete<T>(url: string, config?: any): Promise<T> {
        const response = await this.client.delete<T>(url, config);
        return response.data;
    }

    /**
     * Upload file with progress tracking
     */
    async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
        const formData = new FormData();
        formData.append('file', file);

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent: any) => {
                if (onProgress && progressEvent.total) {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(progress);
                }
            },
        };

        const response = await this.client.post<T>(url, formData, config);
        return response.data;
    }
}

// Create and export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
